import React from "react";

const RichTextRenderer = ({ content }) => {
  if (!content || !Array.isArray(content)) return null;

  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  const imageWidth = isMobile ? "350px" : "900px";

  const renderNode = (node, key) => {
    if (!node || typeof node !== "object") return null;

    // Text node with formatting
    if (node.type === "text") {
      let el = node.text ?? "";
      if (node.format) {
        if (node.format & 1) el = <strong key={key}>{el}</strong>;
        if (node.format & 2) el = <em key={key}>{el}</em>;
        if (node.format & 4) el = <u key={key}>{el}</u>;
        if (node.format & 8) el = <s key={key}>{el}</s>;
        if (node.format & 16)
          el = (
            <code
              key={key}
              className="bg-gray-100 px-1 rounded font-mono text-sm"
            >
              {el}
            </code>
          );
      }
      return el;
    }

    switch (node.type) {
      case "paragraph":
        return (
          <p key={key} className="mb-2">
            {node.children?.map((child, idx) =>
              renderNode(child, `${key}-${idx}`),
            )}
          </p>
        );

      case "heading": {
        const Tag = node.tag || "h2";
        return (
          <Tag key={key} className="mt-4 mb-2 font-bold">
            {node.children?.map((child, idx) =>
              renderNode(child, `${key}-${idx}`),
            )}
          </Tag>
        );
      }

      case "blockquote":
        return (
          <blockquote
            key={key}
            className="border-l-4 border-gray-400 pl-4 italic text-gray-700 my-3"
          >
            {node.children?.map((child, idx) =>
              renderNode(child, `${key}-${idx}`),
            )}
          </blockquote>
        );

      case "code":
      case "codeblock":
        return (
          <pre
            key={key}
            className="bg-gray-900 text-gray-100 p-3 rounded-lg overflow-x-auto my-3"
          >
            <code>
              {node.children?.map((child, idx) =>
                renderNode(child, `${key}-${idx}`),
              )}
            </code>
          </pre>
        );

      case "link":
      case "autolink": {
        const url = node.fields?.url || node.href || "#";
        const newTab = node.fields?.newTab ?? true;
        return (
          <a
            key={key}
            href={url}
            target={newTab ? "_blank" : "_self"}
            rel="noopener noreferrer"
            className="text-blue-600 underline"
          >
            {node.children?.map((child, idx) =>
              renderNode(child, `${key}-${idx}`),
            )}
          </a>
        );
      }

      case "list": {
        const Tag = node.listType === "bullet" ? "ul" : "ol";
        return (
          <Tag
            key={key}
            className={`${
              Tag === "ul" ? "list-disc" : "list-decimal"
            } list-inside ml-5 mb-2`}
          >
            {node.children?.map((child, idx) =>
              renderNode(child, `${key}-${idx}`),
            )}
          </Tag>
        );
      }

      case "listitem":
        return (
          <li key={key}>
            {node.children?.map((child, idx) =>
              renderNode(child, `${key}-${idx}`),
            )}
          </li>
        );

      case "linebreak":
        return <br key={key} />;

      case "upload": {
        const file = node.value || node.fields?.file;

        if (!file) return null;

        const isImage =
          file.url?.match(/\.(jpeg|jpg|png|gif|webp|svg)$/i) ||
          file.mimeType?.startsWith("image/");

        const isVideo =
          file.url?.match(/\.(mp4|webm|ogg)$/i) ||
          file.mimeType?.startsWith("video/");

        if (isImage) {
          return (
            <img
              key={key}
              src={file.url}
              alt={file.alt || ""}
              style={{
                maxWidth: imageWidth,
                width: "100%",
                height: "auto",
              }}
              className="my-3 rounded"
            />
          );
        }

        if (isVideo) {
          return (
            <video
              key={key}
              src={file.url}
              controls
              className="my-3 max-w-full rounded"
            />
          );
        }

        return (
          <a
            key={key}
            href={file.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline"
          >
            {file.filename || file.alt || "Download file"}
          </a>
        );
      }

      case "youtubeEmbed": {
        const url = node.fields?.url || node.value?.url;
        if (!url) return null;
        return (
          <div
            key={key}
            className="my-4 aspect-video w-full overflow-hidden rounded-lg"
          >
            <iframe
              src={url}
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
            />
          </div>
        );
      }

      default:
        if (Array.isArray(node.children)) {
          return (
            <span key={key}>
              {node.children.map((child, idx) =>
                renderNode(child, `${key}-${idx}`),
              )}
            </span>
          );
        }
        return node.text ?? null;
    }
  };

  return <>{content.map((node, idx) => renderNode(node, idx))}</>;
};

export default RichTextRenderer;
